import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLangDropdownOpen, setIsLangDropdownOpen] = useState(false);
  const { language, changeLanguage, t } = useLanguage();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => setIsLangDropdownOpen(false);
    if (isLangDropdownOpen) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [isLangDropdownOpen]);

  const languages = [
    { code: 'en', name: 'English' },
    { code: 'ar', name: 'العربية' },
    { code: 'ku', name: 'کوردی' }
  ];

  const navLinks = [
    { path: '/', label: t('nav.home') },
    { path: '/projects', label: t('nav.projects') },
    { path: '/about', label: t('nav.about') },
    { path: '/services', label: t('nav.services') },
    { path: '/media', label: t('nav.media') },
    { path: '/careers', label: t('nav.careers') },
    { path: '/contact', label: t('nav.contact') }
  ];

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
      isScrolled 
        ? 'bg-black/90 backdrop-blur-md py-4 border-b border-white/10' 
        : 'bg-transparent py-6'
    }`}>
      <div className="container mx-auto px-6 lg:px-12">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center group">
            <img
              src="https://customer-assets.emergentagent.com/job_f5e23208-e2e1-478e-b660-88f2702726f3/artifacts/s61bg8wu_erasebg-transformed.png"
              alt="Darwazay Garmyan"
              className="h-8 sm:h-10 w-auto brightness-0 invert transition-transform duration-300 group-hover:scale-105"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`relative text-sm uppercase tracking-[0.1em] font-light transition-all duration-300 whitespace-nowrap px-1 ${
                  location.pathname === link.path
                    ? 'text-white'
                    : 'text-white/60 hover:text-white'
                }`}
              >
                {link.label}
                {location.pathname === link.path && (
                  <span className="absolute -bottom-1 left-0 w-full h-px bg-white" />
                )}
              </Link>
            ))}
          </div>

          {/* Desktop Controls */}
          <div className="hidden lg:flex items-center space-x-4">
            {/* Language Selector */}
            <div className="relative">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setIsLangDropdownOpen(!isLangDropdownOpen);
                }}
                className="flex items-center space-x-2 px-4 py-2 border border-white/20 text-white/80 hover:border-white/40 hover:text-white transition-all duration-300"
              >
                <span className="text-xs uppercase tracking-widest">
                  {language}
                </span>
                <ChevronDown className={`w-3 h-3 transition-transform duration-300 ${isLangDropdownOpen ? 'rotate-180' : ''}`} />
              </button>

              {isLangDropdownOpen && (
                <div className="absolute top-full mt-2 right-0 bg-black/95 backdrop-blur-md border border-white/10 py-1 min-w-[140px] animate-fade-in-down">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => {
                        changeLanguage(lang.code);
                        setIsLangDropdownOpen(false);
                      }}
                      className={`w-full px-4 py-3 text-left text-xs uppercase tracking-widest hover:bg-white/10 transition-colors ${
                        language === lang.code ? 'bg-white/10 text-white' : 'text-white/60'
                      }`}
                    >
                      {lang.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden p-2 text-white transition-colors"
          >
            <Menu className="w-6 h-6" />
          </button>
        </div>
      </div>

      {/* Mobile Menu - Using Portal to render outside nav for proper z-index */}
      {isMobileMenuOpen && createPortal(
        <div 
          className="lg:hidden fixed top-0 left-0 right-0 bottom-0 bg-black overflow-hidden"
          style={{ zIndex: 99999 }}
        >
          {/* Close Button */}
          <button
            onClick={() => setIsMobileMenuOpen(false)}
            className="absolute top-6 right-6 w-12 h-12 flex items-center justify-center text-white hover:bg-white/10 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>

          {/* Menu Content */}
          <div className="flex flex-col items-center justify-center h-full px-8">
            {/* Logo */}
            <img
              src="https://customer-assets.emergentagent.com/job_f5e23208-e2e1-478e-b660-88f2702726f3/artifacts/s61bg8wu_erasebg-transformed.png"
              alt="Darwazay Garmyan"
              className="h-10 w-auto brightness-0 invert mb-12"
            />

            {/* Menu Items */}
            <div className="flex flex-col items-center space-y-6 mb-12">
              {navLinks.map((link, index) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`text-2xl sm:text-3xl font-light tracking-wide transition-all duration-300 animate-fade-in-up ${
                    location.pathname === link.path
                      ? 'text-white'
                      : 'text-white/50 hover:text-white'
                  }`}
                  style={{ animationDelay: `${index * 0.05}s` }}
                >
                  {link.label}
                </Link>
              ))}
            </div>
            
            {/* Language Selector */}
            <div className="flex gap-3 animate-fade-in-up" style={{ animationDelay: '0.4s' }}>
              {languages.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => {
                    changeLanguage(lang.code);
                    setIsMobileMenuOpen(false);
                  }}
                  className={`px-6 py-3 text-xs uppercase tracking-widest font-light border transition-all duration-300 ${
                    language === lang.code
                      ? 'bg-white text-black border-white'
                      : 'bg-transparent text-white/60 border-white/20 hover:border-white/50 hover:text-white'
                  }`}
                >
                  {lang.code}
                </button>
              ))}
            </div>
          </div>

          {/* Decorative Lines */}
          <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" />
        </div>,
        document.body
      )}
    </nav>
  );
};

export default Navbar;
